import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.playsInline = true;
    videoElement.setAttribute('muted', '');
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('webkit-playsinline', '');
    videoElement.load();

    const tryPlay = () => {
      if (!videoElement.paused) return;
      const promise = videoElement.play();
      if (promise && promise.catch) promise.catch(() => {});
    };

    videoElement.addEventListener('loadeddata', tryPlay, { once: true });
    videoElement.addEventListener('canplay', tryPlay, { once: true });
    tryPlay();

    const onInteraction = () => {
      tryPlay();
      if (!videoElement.paused) {
        ['touchstart', 'pointerdown', 'click', 'keydown'].forEach((type) =>
          document.removeEventListener(type, onInteraction, true)
        );
      }
    };
    ['touchstart', 'pointerdown', 'click', 'keydown'].forEach((type) =>
      document.addEventListener(type, onInteraction, { capture: true, passive: true })
    );

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') tryPlay();
    });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
